'use client';

import * as React from 'react';

// UI
import { Typography } from '@components/typography';
import { ReviewStars } from './review-stars';
import { Link } from '@components/link';
import {
  ListingReviewSummarySource,
  ListingReviewSummariesListAllForListingQuery,
} from '@gql';

type OtaReviewData = Extract<
  ListingReviewSummariesListAllForListingQuery['listingReviewSummariesListAllForListing'],
  { __typename: 'ListingReviewSummaryCollection' }
>['entries'][number];

type Props = {
  otaReviews: OtaReviewData[];
  airbnbUrl?: string;
  vrboUrl?: string;
  isLoading: boolean;
};

const sourceConfig = {
  [ListingReviewSummarySource.Airbnb]: {
    logoName: 'airbnb',
    label: 'Read on Airbnb',
  },
  [ListingReviewSummarySource.Vrbo]: {
    logoName: 'vrbo',
    label: 'Read on VRBO',
  },
} as const;

const getSourceConfig = (airbnbUrl?: string, vrboUrl?: string) => ({
  [ListingReviewSummarySource.Airbnb]: {
    ...sourceConfig[ListingReviewSummarySource.Airbnb],
    url: airbnbUrl || undefined,
  },
  [ListingReviewSummarySource.Vrbo]: {
    ...sourceConfig[ListingReviewSummarySource.Vrbo],
    url: vrboUrl || undefined,
  },
});

export const OtaReviewSummaries: React.FC<Props> = ({
  otaReviews,
  airbnbUrl = undefined,
  vrboUrl = undefined,
  isLoading,
}) => {
  if (isLoading || otaReviews.length === 0) {
    return null;
  }

  const sourceConfig = getSourceConfig(airbnbUrl, vrboUrl);

  return (
    <>
      {otaReviews.map((otaReview) => {
        const config =
          sourceConfig[otaReview.source as keyof typeof sourceConfig];

        if (!config) {
          return null;
        }

        if (otaReview.count === 0 || otaReview.averageRating === null) {
          return null;
        }

        return (
          <div
            key={`${otaReview.source}-${otaReview.count}`}
            className="space-y-2"
          >
            <div className="flex items-center space-x-4">
              {/* Circular Avatar with layered circles */}
              <div className="flex-shrink-0 relative w-10 h-10">
                <div className="absolute w-10 h-10 bg-primary-xlight/20 rounded-full left-2.5 z-0 border border-white" />
                <div className="absolute w-10 h-10 bg-primary-xlight/50 rounded-full left-1.5 z-10 border border-white" />
                <div className="absolute w-10 h-10 bg-primary-xlight rounded-full flex items-center justify-center z-20 border-r border-white">
                  <Typography
                    variant="body1"
                    component="span"
                    className="text-primary-main font-medium"
                  >
                    {otaReview.count}
                  </Typography>
                </div>
              </div>

              <div className="flex-1 space-y-1">
                <div className="flex items-center space-x-2">
                  <Typography
                    variant="body1"
                    component="span"
                    className="text-primary-main"
                  >
                    Guest ratings from
                  </Typography>
                  <img
                    src={`/assets/img/otas/${config.logoName}-logo-long.png`}
                    alt={`${otaReview.source} logo`}
                    className="h-4 w-auto"
                  />
                </div>

                {otaReview.averageRating && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Typography
                        variant="small"
                        variantM="body2"
                        component="span"
                        className="text-sm md:text-base font-medium text-primary-main leading-none"
                      >
                        {otaReview.averageRating?.toFixed(2)}
                      </Typography>
                      <ReviewStars rating={otaReview.averageRating} />
                    </div>

                    <div className="hidden sm:flex items-center text-gray-main">
                      &bull;
                    </div>

                    <Typography
                      variant="small"
                      variantM="body2"
                      component="span"
                      className="text-gray-main leading-none"
                    >
                      Average rating based on {otaReview.count} reviews
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            <div>
              {config.url && (
                <Link
                  to={{ href: config.url, as: config.url }}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-primary-main underline cursor-pointer font-medium text-sm"
                >
                  {config.label}
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};
